// apps/web/src/lib/pendingOnboarding.ts
// Answers collected in the logged-out Get Started wizard are held here until an
// account exists. They survive the OAuth round-trip (Google reloads the app),
// so the post-auth gate can submit them automatically. Cleared on submit or if
// the user abandons and signs in as an existing account.
import type { Sex, GoalType } from "@shared/types";
import type { ActivityLevel } from "@shared/onboarding";

const KEY = "caloly.pending_onboarding";

export interface PendingOnboarding {
  name?: string;
  sex: Sex;
  activity?: ActivityLevel;
  birthday?: string;          // YYYY-MM-DD
  age: number;
  height_cm: number;
  weight_kg: number;
  target_weight_kg: number;
  goal_type: GoalType;
  nutrition_pref?: string;
  event_name?: string;
  event_date?: string;
  referral_source?: string;
  tried_tracking?: boolean;
  daily_calories?: number;
  protein_g?: number;
  measured_on: string;        // YYYY-MM-DD (client local "today")
  /** ms epoch when saved — the gate ignores answers older than a short window
   *  so a leftover set never lands on the wrong (later) account. */
  savedAt?: number;
}

export function savePending(p: PendingOnboarding): void {
  try { localStorage.setItem(KEY, JSON.stringify({ ...p, savedAt: Date.now() })); } catch { /* private mode */ }
}

/** Fresh = saved within the last 30 minutes. Older pending is treated as
 *  abandoned so it can't attach to whatever account signs in next. */
export function loadFreshPending(maxAgeMs = 30 * 60 * 1000): PendingOnboarding | null {
  const p = loadPending();
  if (p && typeof p.savedAt === "number" && Date.now() - p.savedAt < maxAgeMs) return p;
  if (p) clearPending();
  return null;
}

export function loadPending(): PendingOnboarding | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PendingOnboarding) : null;
  } catch { return null; }
}

export function clearPending(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
